import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from "react-redux";
import cn from 'classnames';
import WaveSurfer from 'wavesurfer.js';
import { Button, Checkbox } from 'rhombus-react';
import { generateAudio } from 'reducers/GenAI';
import Card from './Card';

const AUDIO_STATUSES = {
	GENERATING: { label: 'Generating', icon: 'fa-regular fa-circle-notch circle-spin', isDisabled: true },
	LOADING: { label: 'Loading', icon: 'fa-regular fa-circle-notch circle-spin', isDisabled: true },
	ERROR: { label: 'Preview Error', icon: 'fa-regular fa-circle-exclamation', isDisabled: true },
	PREVIEW: { label: 'Preview', icon: 'fa-regular fa-circle-play', isDisabled: false },
	STOP: { label: 'Stop', icon: 'fa-regular fa-circle-stop', isDisabled: false },
};

const PreviewCard = ({ index, libraryId, version = {}, isSelected, onSelect, forceSelect, audiosContainerRef }) => {
	const { scriptText, voice, speed, backgroundSound } = version;

	const [isExpanded, setIsExpanded] = useState(false);
	const [audioStatus, setAudioStatus] = useState();
	const [audioUrl, setAudioUrl] = useState();
	const audioRef = useRef();
	const waveRef = useRef();
	const dispatch = useDispatch();

	const label = `Audio ${index + 1} - ${voice.name} ${backgroundSound ? `- ${backgroundSound.name}` : ''}`;
	const voiceTags = [voice.gender, voice.age, voice.accent, ...(voice.tags || [])];
	const backgroundSoundTags = backgroundSound ? [
		...(backgroundSound.moods || []),
		...(backgroundSound.genres || []),
		...(backgroundSound.instruments || [])
	] : [];

	useEffect(() => {
		setAudioStatus(AUDIO_STATUSES.GENERATING);
		const data = {
			creativeLibraryId: libraryId,
			text: scriptText,
			voice: voice.alias,
		};
		if (speed) {
			data.speed = speed;
		}
		if (backgroundSound) {
			data.backgroundSound = backgroundSound.alias;
		}
		dispatch(generateAudio(data)).then((resp) => {
			const url = resp.payload?.data?.audioUrl;
			if (resp.success && url) {
				setAudioStatus(AUDIO_STATUSES.LOADING);
				setAudioUrl(url);
				if (forceSelect) {
					onSelect(url);
				}
			} else {
				setAudioStatus(AUDIO_STATUSES.ERROR);
			}
		});
	}, []);

	const onAudioStatusClick = () => {
		if (audioStatus === AUDIO_STATUSES.PREVIEW) {
			audioRef.current?.play();
		} else {
			audioRef.current?.pause();
		}
	};

	const onAudioLoaded = () => {
		setAudioStatus(AUDIO_STATUSES.PREVIEW);
	};

	useEffect(() => {
		const onPlay = () => {
			Array.from(audiosContainerRef.current?.getElementsByTagName('audio')).forEach((audioElement) => {
				if (audioElement !== audioRef.current && !audioElement.paused) {
					audioElement.pause();
				}
			});
			setAudioStatus(AUDIO_STATUSES.STOP);
		};
		const onPause = () => setAudioStatus(AUDIO_STATUSES.PREVIEW);
		audioRef.current?.addEventListener('play', onPlay);
		audioRef.current?.addEventListener('pause', onPause);
		return () => {
			audioRef.current?.removeEventListener('play', onPlay);
			audioRef.current?.removeEventListener('pause', onPause);
		};
	}, [audioRef.current]);

	useEffect(() => {
		let ws;
		if (waveRef.current && audioRef.current && audioUrl) {
			ws = WaveSurfer.create({
				container: waveRef.current,
				media: audioRef.current,
				height: 50,
				mediaControls: true,
				normalize: true,
				waveColor: '#858c90',
				progressColor: '#4a4a4a',
				cursorColor: '#4a4a4a',
			});
		}
		return () => {
			ws?.destroy();
		};
	}, [audioUrl]);

	const header = (
		<>
			{forceSelect && label}
			{!forceSelect && (
				<Checkbox
					key={audioUrl}
					type="radio"
					label={label}
					value={index}
					checked={isSelected}
					disabled={!audioUrl}
					onChange={() => onSelect(audioUrl)}
				/>
			)}
			<span>
				{audioStatus && (
					<Button
						className="gen-audio-button"
						icon={audioStatus.icon}
						onClick={onAudioStatusClick}
						disabled={audioStatus.isDisabled}
					>
						{audioStatus.label}
					</Button>
				)}
				<Button
					className="gen-audio-button"
					icon={`fa-regular fa-eye${isExpanded ? '-slash' : ''}`}
					onClick={() => setIsExpanded(prev => !prev)}
				>
					{`${isExpanded ? 'Hide' : 'View'} Details`}
				</Button>
			</span>
		</>
	);

	return (
		<Card
			header={header}
			isExpanded={isExpanded}
			className={cn('preview-card', { 'preview-card-selected': isSelected })}
		>
			<p className="script-text">{scriptText}</p>
			<div className="preview-card-row">
				The selected voice <b>({voice.name})</b> is:
			</div>
			<div className="preview-card-row">
				{voiceTags.map(tag => (
					<span key={tag} className="audio-tag">{tag}</span>
				))}
			</div>
			{backgroundSound && (
				<>
					<div className="preview-card-row">
						The selected track <b>({backgroundSound.name})</b> is:
					</div>
					<div className="preview-card-row">
						{backgroundSoundTags.map(tag => (
							<span key={tag} className="audio-tag audio-tag-sound">{tag}</span>
						))}
					</div>
				</>
			)}
			{audioUrl && (
				<div className="preview-card-row">
					<div ref={waveRef} />
					<audio
						ref={audioRef}
						src={audioUrl}
						controls
						preload="auto"
						onLoadedData={onAudioLoaded}
					/>
				</div>
			)}
		</Card>
	);
};

export default PreviewCard;
